import { shallowRef } from 'vue'
import type { StationWeekList } from '@/Network/Types/Responses/StationResponses'
import type { LensColorBy } from '@/Network/Types/Requests/DashboardSavedViewRequests'
import { DoctorStationStatus } from '@/translations/doctor.station.status'
import { stationPalette, tint, type CardColors, type CheckColorKey } from '@/theme/stationPalette'

export { tint }
export type { CardColors, CheckColorKey }

// Paleta do build (flag VUE_APP_ENABLED_STATION_PALETTE_V3): avaliada uma vez, na importação.
const PALETTE = stationPalette()
const colors = ({ stripe, bg }: CardColors): CardColors => ({ stripe, bg })

/** Vaga aberta: cor própria em qualquer lente (laranja na paleta antiga, verde na v3; spec §5.2). */
export const OPEN_STATION_COLORS: CardColors = colors(PALETTE.open)

/** Paleta categórica acessível (sem laranja, reservado à vaga). Mesma dos mockups G6/L5. */
export const CATEGORICAL_PALETTE: readonly string[] = [
  '#7C3AED',
  '#1D4ED8',
  '#0F766E',
  '#C026D3',
  '#65A30D',
  '#9F1239',
  '#475569',
  '#0891B2',
  '#BE185D',
  '#0369A1'
]

export const NO_VALUE_COLOR = '#9CA3AF'

/** FNV-1a: estável entre sessões e navegadores. */
function hashKey(key: string): number {
  let hash = 2166136261
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return Math.abs(hash)
}

const hashIndex = (key: string): number => hashKey(key) % CATEGORICAL_PALETTE.length

/**
 * Cores distintas para as chaves que estão na tela: cada chave fica com a cor do hash quando
 * ela está livre (estável entre telas) e, se colidir, com a próxima cor livre da paleta. As
 * chaves são ordenadas antes, então o resultado não depende da ordem dos plantões. Com mais
 * chaves do que cores, as que sobram voltam para a cor do hash.
 */
export function assignCategoricalColors(keys: Iterable<string>): Map<string, string> {
  const size = CATEGORICAL_PALETTE.length
  const out = new Map<string, string>()
  const used = new Set<number>()
  const pending: string[] = []
  ;[...new Set(keys)].sort().forEach((key) => {
    const index = hashIndex(key)
    if (used.has(index)) {
      pending.push(key)
      return
    }
    used.add(index)
    out.set(key, CATEGORICAL_PALETTE[index])
  })
  pending.forEach((key) => {
    const start = hashIndex(key)
    let chosen = start
    for (let step = 1; step < size; step += 1) {
      const index = (start + step) % size
      if (!used.has(index)) {
        chosen = index
        break
      }
    }
    used.add(chosen)
    out.set(key, CATEGORICAL_PALETTE[chosen])
  })
  return out
}

// Atribuição da tela atual (setores e especialidades em grupos separados). Reativa: cards e
// legenda recalculam a cor quando a lista de plantões muda.
const screenColors = shallowRef<ReadonlyMap<string, string>>(new Map())

/** Cada grupo (ex.: setores, especialidades) disputa a paleta inteira de forma independente. */
export function setCategoricalColorGroups(...groups: Iterable<string>[]): void {
  const merged = new Map<string, string>()
  groups.forEach((group) => assignCategoricalColors(group).forEach((c, k) => merged.set(k, c)))
  screenColors.value = merged
}

export function categoricalColor(key: string): string {
  return screenColors.value.get(key) ?? CATEGORICAL_PALETTE[hashIndex(key)]
}

const CHECK_LABELS: Record<CheckColorKey, string> = {
  ok: 'Check-in e check-out ok',
  ongoing: 'Check-in ok (em andamento)',
  late: 'Com atraso ou saída antecipada',
  noout: 'Sem check-out',
  miss: 'Check-in não realizado',
  future: 'A realizar'
}

/**
 * `ongoing` = check-in feito e plantão em andamento (ainda sem check-out): não é "concluído".
 * A cor é a faixa da lente Check da paleta (antiga: saturada; v3: pastel).
 */
export const CHECK_COLORS: Record<CheckColorKey, { color: string; label: string }> =
  Object.fromEntries(
    (Object.keys(CHECK_LABELS) as CheckColorKey[]).map((key) => [
      key,
      { color: PALETTE.checkLens[key].stripe, label: CHECK_LABELS[key] }
    ])
  ) as Record<CheckColorKey, { color: string; label: string }>

export function checkColorKey(status?: DoctorStationStatus): CheckColorKey {
  switch (status) {
    case DoctorStationStatus.CHECKOUT_REALIZADO:
      return 'ok'
    case DoctorStationStatus.CHECKIN_REALIZADO:
      return 'ongoing'
    case DoctorStationStatus.CHECKIN_REALIZADO_COM_ATRASO:
    case DoctorStationStatus.CHECKOUT_REALIZADO_COM_ANTECEDENCIA:
      return 'late'
    case DoctorStationStatus.CHECKOUT_NAO_REALIZADO:
    case DoctorStationStatus.DISPONIBILIZADO_PARA_CHECKOUT:
      return 'noout'
    case DoctorStationStatus.CHECKIN_NAO_REALIZADO:
      return 'miss'
    default:
      return 'future'
  }
}

export const TYPE_COLORS: Record<'FIXO' | 'COBERTURA' | 'EXTRA', CardColors> = {
  FIXO: colors(PALETTE.type.FIXO),
  COBERTURA: colors(PALETTE.type.COBERTURA),
  EXTRA: colors(PALETTE.type.EXTRA)
}

/** Plantão de médico fora da equipe (`ourTeam = false`): cinza. */
export const OFF_TEAM_COLORS: CardColors = colors(PALETTE.offTeam)

/** Lente "Equipe × externos" (SPEC-v3 §9): nossa equipe × médicos de outra equipe. */
export const TEAM_LENS_COLORS: { ours: CardColors; others: CardColors } = {
  ours: colors(PALETTE.teamLens.ours),
  others: colors(PALETTE.teamLens.others)
}

/** Cores de "Tipo" em hex, para os cards novos (Compacta/Mínima) que não usam as classes do withIcon. */
export function typeColors(card: StationWeekList): CardColors {
  if (card.opened) return OPEN_STATION_COLORS
  if (!card.ourTeam) return OFF_TEAM_COLORS
  const cell = card.cell === 'COBERTURA' || card.cell === 'EXTRA' ? card.cell : 'FIXO'
  return TYPE_COLORS[cell]
}

/**
 * Alerta do ds-attendance. Paleta antiga: vermelho sólido, como o `withIcon` (texto branco por
 * cima); v3: fundo vermelho pastel com texto vermelho escuro.
 */
export const ALERT_COLORS: CardColors = colors(PALETTE.alert)

/**
 * Texto dos cards Compacta/Mínima. Paleta antiga: classes de hoje (laranja da vaga, branco no
 * alerta vermelho cheio). v3: sem classe, cor por style (verde escuro / vermelho escuro).
 */
const IS_PALETTE_V3 = PALETTE.id === 'pastel-v3'
export const OPEN_TEXT_CLASS = IS_PALETTE_V3 ? '' : 'text-feedBackAlert'
export const OPEN_TEXT_STYLE = IS_PALETTE_V3 ? { color: PALETTE.open.text } : undefined
export const ALERT_TEXT_CLASS = IS_PALETTE_V3 ? '' : 'text-white'
export const ALERT_TEXT_STYLE = IS_PALETTE_V3 ? { color: PALETTE.alert.text } : undefined

/**
 * Card em alerta? Mesma condição do `withIcon` (`flags.enabledDsAttendancePolicy && isAlert`);
 * vaga aberta não tem alerta (é o `vacancy`, não o `withIcon`).
 */
export const isCardAlert = (card: StationWeekList, alertEnabled: boolean): boolean =>
  alertEnabled && !card.opened && !!card.isAlert

/**
 * Cor dos cards novos (Compacta/Mínima): o alerta vermelho prevalece sobre a lente e o tipo,
 * como no `withIcon` do Confortável; depois a lente; por fim as cores de "Tipo".
 */
export function denseCardColors(
  card: StationWeekList,
  colorBy: LensColorBy,
  alertEnabled: boolean
): CardColors {
  if (isCardAlert(card, alertEnabled)) return ALERT_COLORS
  return resolveCardColors(card, colorBy) ?? typeColors(card)
}

const withTint = (color: string): CardColors => ({ stripe: color, bg: tint(color, 0.07) })

export const sectorColorKey = (card: StationWeekList): string | null =>
  card.sector ? `s:${card.sector.id}` : null

export const specialtyColorKey = (card: StationWeekList): string | null =>
  card.specialty ? `${card.specialty.source === 'official' ? 'o' : 'c'}:${card.specialty.id}` : null

/** `null` = manter a cor padrão do componente (lente "Tipo"). */
export function resolveCardColors(card: StationWeekList, colorBy: LensColorBy): CardColors | null {
  if (colorBy === 'tipo') return null
  if (card.opened) return OPEN_STATION_COLORS
  if (colorBy === 'setor') {
    const key = sectorColorKey(card)
    return withTint(key ? categoricalColor(key) : NO_VALUE_COLOR)
  }
  if (colorBy === 'especialidade') {
    const key = specialtyColorKey(card)
    return withTint(key ? categoricalColor(key) : NO_VALUE_COLOR)
  }
  if (colorBy === 'equipe')
    return { ...(card.ourTeam ? TEAM_LENS_COLORS.ours : TEAM_LENS_COLORS.others) }
  // Paleta antiga: tint de 7% da cor (igual a antes); v3: o par pastel exato da S1.
  return { ...PALETTE.checkLens[checkColorKey(card.status)] }
}
